import { useState } from "react";
import { toast } from "sonner";
import { X, Twitter, Linkedin, Facebook, MessageCircle, Link as LinkIcon, Check } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";

const APP_URL = "https://foundtheroom.com";

const buildShareText = (sessionsCompleted, hostName) => {
  const first = sessionsCompleted <= 1;
  if (first) {
    return `Just held my first board meeting — with myself.\n\nBoardroom turns weekly reflection into a real ritual: wins, losses, resolutions, signed off. It's accountability that feels like leadership.\n\n${APP_URL}`;
  }
  return `${sessionsCompleted} board meetings deep. Weekly reflection has become non-negotiable.\n\nBoardroom is where I run my check-ins — wins, losses, action items, signed off.\n\n${APP_URL}`;
};

const shareLinks = (text) => {
  const enc = encodeURIComponent(text);
  return {
    twitter: `https://twitter.com/intent/tweet?text=${enc}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(APP_URL)}&summary=${enc}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(APP_URL)}&quote=${enc}`,
    whatsapp: `https://api.whatsapp.com/send?text=${enc}`,
  };
};

/**
 * First-session share card. Shows on the Meeting Minutes page after the user
 * completes their first board meeting. Any action (share or dismiss) marks the
 * prompt as shown so we don't nag on the second session.
 */
export default function ShareFirstSessionCard({ onDismiss, sessionsCompleted, hostName }) {
  const [copied, setCopied] = useState(false);
  const text = buildShareText(sessionsCompleted, hostName);
  const links = shareLinks(text);

  const markShown = async () => {
    try {
      await api.post("/share/mark-shown");
    } catch (e) {
      // Non-fatal — the prompt just may reappear if the request failed.
      console.debug("ShareFirstSessionCard: mark-shown failed", e);
    }
  };

  const openShare = (url, platform) => {
    window.open(url, "_blank", "noopener,noreferrer,width=640,height=560");
    markShown();
    toast.success(`Opening ${platform}`);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${text}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
      markShown();
      toast.success("Copied — paste anywhere");
    } catch {
      toast.error("Couldn't copy. Long-press to select.");
    }
  };

  const dismiss = () => {
    markShown();
    onDismiss?.();
  };

  return (
    <div
      className="relative bg-[#121212] border border-[#c89f45]/40 rounded-lg p-6 my-6 shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
      data-testid="share-first-session-card"
    >
      <button
        onClick={dismiss}
        className="absolute top-4 right-4 text-[#666] hover:text-[#fafafa] transition-colors"
        aria-label="Dismiss share prompt"
        data-testid="share-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <p className="text-[11px] tracking-[3px] uppercase text-[#c89f45] font-medium mb-3">
        First session in the books
      </p>
      <h3 className="font-serif text-2xl text-[#fafafa] mb-2">You're on the Founders Wall.</h3>
      <p className="text-sm text-[#aaa] leading-relaxed mb-5 max-w-lg">
        A meaningful ritual becomes a movement when people know it exists. If Boardroom is helping
        you show up for yourself, sharing the ritual invites others to do the same.
      </p>

      <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-md p-4 mb-5 text-sm text-[#ddd] leading-relaxed whitespace-pre-wrap" data-testid="share-preview">
        {text}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => openShare(links.twitter, "X")}
          className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-[#fafafa] border border-[#2a2a2a] rounded-md h-9 px-3 text-sm gap-2"
          data-testid="share-twitter"
        >
          <Twitter className="w-4 h-4" /> X
        </Button>
        <Button
          onClick={() => openShare(links.linkedin, "LinkedIn")}
          className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-[#fafafa] border border-[#2a2a2a] rounded-md h-9 px-3 text-sm gap-2"
          data-testid="share-linkedin"
        >
          <Linkedin className="w-4 h-4" /> LinkedIn
        </Button>
        <Button
          onClick={() => openShare(links.facebook, "Facebook")}
          className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-[#fafafa] border border-[#2a2a2a] rounded-md h-9 px-3 text-sm gap-2"
          data-testid="share-facebook"
        >
          <Facebook className="w-4 h-4" /> Facebook
        </Button>
        <Button
          onClick={() => openShare(links.whatsapp, "WhatsApp")}
          className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-[#fafafa] border border-[#2a2a2a] rounded-md h-9 px-3 text-sm gap-2"
          data-testid="share-whatsapp"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </Button>
        <Button
          onClick={copyLink}
          className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md h-9 px-3 text-sm gap-2 ml-auto"
          data-testid="share-copy"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" /> Copied
            </>
          ) : (
            <>
              <LinkIcon className="w-4 h-4" /> Copy
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
